import { useAuthStore } from '@/stores/auth-store.ts';
import { useLDClient } from 'launchdarkly-vue-client-sdk';
import { watch } from 'vue';

/* ---------------------------------------------------------------------------------------------- */

/**
 * Composable for syncing LaunchDarkly context with user authentication state.
 *
 * When the user authenticates, this updates LaunchDarkly with a user-specific context
 * containing their profile information for feature flag targeting.
 *
 * When the user logs out, it resets to an anonymous context.
 */
export function useLaunchDarklyContext() {
   const authStore = useAuthStore();
   const ldClient = useLDClient();

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Update LaunchDarkly context to use the authenticated user's profile
    */
   const identifyUser = async () => {
      const profile = authStore.userProfile;

      if (!profile) {
         console.warn('[LaunchDarkly] Cannot identify user: no profile available');
         return;
      }

      await ldClient.identify({
         kind: 'user',
         key: profile.user_id,
         email: profile.email,
         name: `${profile.firstname} ${profile.lastname}`.trim() || undefined,
         firstName: profile.firstname || undefined,
         lastName: profile.lastname || undefined,
         // Custom attributes for targeting
         organization: profile.organization,
         orgId: profile.org_id,
         userType: profile.user_type,
         orgUserRole: profile.org_user_role,
         city: profile.city || undefined,
         state: profile.state || undefined,
         country: profile.country || undefined,
         premium: profile.user_type === 'premium',
      });
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Reset LaunchDarkly context to anonymous
    */
   const resetToAnonymous = async () => {
      await ldClient.identify({
         kind: 'user',
         anonymous: true,
         key: 'anonymous-user',
         premium: false,
      });
   };

   /* ------------------------------------------------------------------------------------------- */

   /**
    * Watch for changes in user profile and update LaunchDarkly context accordingly.
    * This should be called once during app initialization.
    */
   const watchAuthState = () => {
      // Watch for userProfile changes (set after authentication + profile fetch)
      watch(
         () => authStore.userProfile,
         async (newProfile, oldProfile) => {
            if (newProfile && !oldProfile) {
               // User just logged in and profile was fetched
               await identifyUser();
            }
            else if (!newProfile && oldProfile) {
               // User just logged out
               await resetToAnonymous();
            }
         },
         { immediate: true },
      );
   };

   /* ------------------------------------------------------------------------------------------- */

   return {
      identifyUser,
      resetToAnonymous,
      watchAuthState,
   };
}
